// Actions for the "Expedientes"

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  Analysis,
  CompleteFileFolderResponse,
  Folder,
  FolderHistory,
  FolderResult,
  Metadata,
  OnAddCjaNotesToFolderRequest,
  OnAddNotesToFolderRequest,
  OnCreateFolderRequest,
  OnDownloadFolderAsZipRequest,
  OnReassignCollaboratorRequest,
  OnReassignCollaboratorResponse,
  OnReassignUserRequest,
  OnReassignUserResponse,
  OnRemoveFolderTemplateRequest,
  OnToggleFolderActiveRequest,
  OnUpdateAnalysisForm,
} from "../interfaces/folder.types.ts";
import { setActiveFolder } from "../slices/folderSlice.ts";
import { setActiveVault } from "../slices/vaultSlice.ts";
import api from "../../api";
import { getExceptionMessage } from "../../exceptions/helpers/getExceptionMessage.ts";
import {
  folderExceptions,
  folderExceptionsMessage,
} from "../../exceptions/default/folderExceptions.ts";

export const setActiveFolderAsync = createAsyncThunk(
  "folders/setActiveFolderAsync",
  async (folder: Folder, thunkAPI) => {
    thunkAPI.dispatch(setActiveFolder(folder));
    thunkAPI.dispatch(setActiveVault(folder));
  },
);

export const fetchAllFolders = createAsyncThunk(
  "folders/fetchAllFolders",
  async (url: string, thunkAPI) => {
    try {
      const response = await api.get<FolderResult>(url);
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.fetchAllFolders,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchFolderById = createAsyncThunk(
  "folders/fetchFolderById",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<Folder>(`/docvault/folders/${id}`);
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.fetchFolderById,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchFolderRequirements = createAsyncThunk(
  "folders/fetchFolderRequirements",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<Folder>(`/docvault/folders/${id}`);
      return response.data.subfolders?.flatMap(
        (subfolder) => subfolder.requirements,
      );
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.fetchFolderRequirements,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const createFolder = createAsyncThunk(
  "folders/createFolder",
  async (body: OnCreateFolderRequest, thunkAPI) => {
    const metadata: Metadata = {
      tenant: {
        name: body.tenant_name,
        first_surname: body.tenant_first_surname,
        email: body.tenant_email,
        phone_number: body.tenant_phone_number,
      },
      advisor: {
        name: body.advisor_name,
        first_surname: body.advisor_first_surname,
        email: body.advisor_email,
        phone_number: body.advisor_phone_number,
      },
    };

    if (body.tenant_second_surname)
      metadata.tenant!.second_surname = body.tenant_second_surname;
    if (body.tenant_curp) metadata.tenant!.curp = body.tenant_curp;
    if (body.tenant_rfc) metadata.tenant!.rfc = body.tenant_rfc;

    if (body.advisor_second_surname)
      metadata.advisor!.second_surname = body.advisor_second_surname;

    if (body.guarantor_email)
      metadata.guarantor = { email: body.guarantor_email };
    if (body.landlord_email) metadata.landlord = { email: body.landlord_email };

    // MB-362: for a renewal, store the previous folio under metadata.renewal.
    if (body.is_renewal && body.previous_folio)
      metadata.renewal = { previous_folio: body.previous_folio };

    try {
      const folder = {
        templates: body.templates,
        metadata,
        // MB-362: forwarded top-level (the backend CreateFolderSerializer accepts it as a
        // dedicated field, not inside metadata). Must be set here explicitly — the thunk
        // reshapes the flat payload and drops anything it does not copy.
        is_renewal: body.is_renewal ?? false,
      };

      const response = await api.post<Folder>("/docvault/folders/", folder);
      return response.data;
    } catch (error: any) {
      // `?.` también en `response`: sin respuesta (red caída) el acceso directo lanzaba un
      // TypeError y el modal acababa enseñando "Cannot read properties of undefined".
      const msg = getExceptionMessage(
        error?.response?.data?.code,
        folderExceptions.createFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const updateFolder = createAsyncThunk(
  "folders/updateFolder",
  async ({ folderId, data }: { folderId: string; data: any }, thunkAPI) => {
    try {
      const response = await api.patch<Folder>(
        `/docvault/folders/${folderId}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.updateFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const addNotesToFolder = createAsyncThunk(
  "folders/addNotesToFolder",
  async ({ id, notes }: OnAddNotesToFolderRequest, thunkAPI) => {
    try {
      const response = await api.patch<Folder>(`/docvault/folders/${id}`, {
        notes,
      });
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.addNotesToFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const addCjaNotesToFolder = createAsyncThunk(
  "folders/addCjaNotesToFolder",
  async ({ id, cja_notes }: OnAddCjaNotesToFolderRequest, thunkAPI) => {
    try {
      const response = await api.patch<Folder>(`/docvault/folders/${id}`, {
        cja_notes: cja_notes,
      });
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.addNotesToFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const toggleFolderActive = createAsyncThunk(
  "folders/toggleFolderActive",
  async ({ id, is_active }: OnToggleFolderActiveRequest, thunkAPI) => {
    try {
      const response = await api.patch<Folder>(`/docvault/folders/${id}`, {
        is_active,
      });
      thunkAPI.dispatch(setActiveFolder(response.data));
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.toggleFolderActive,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchFolderHistory = createAsyncThunk(
  "folders/fetchFolderHistory",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<FolderHistory>(
        `/docvault/folders/${id}/history-records/`,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.fetchFolderHistory,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onReassignUser = createAsyncThunk(
  "folders/onReassignUser",
  async ({ folder_id, user_id }: OnReassignUserRequest, thunkAPI) => {
    try {
      const response = await api.patch<OnReassignUserResponse>(
        `/docvault/folders/${folder_id}/reassign`,
        {
          new_assigned_user_id: user_id,
        },
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.onReassignUser,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onReassignCollaborator = createAsyncThunk(
  "folders/onReassignCollaborator",
  async ({ folder_id, user_id }: OnReassignCollaboratorRequest, thunkAPI) => {
    try {
      const response = await api.patch<OnReassignCollaboratorResponse>(
        `/docvault/folders/${folder_id}/reassign-collaborator`,
        {
          new_collaborator_id: user_id,
        },
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.onReassignCollaborator,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onUpdateAnalysisForm = createAsyncThunk(
  "folders/onUpdateAnalysisForm",
  async ({ folder_id, form_values }: OnUpdateAnalysisForm, thunkAPI) => {
    try {
      const response = await api.patch<Analysis>(
        `/docvault/folders/${folder_id}/analysis`,
        {
          ...form_values,
        },
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.updateAnalysisForm,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onRemoveFolderTemplate = createAsyncThunk(
  "folders/onRemoveFolderTemplate",
  async (
    { folder_id, template_slug }: OnRemoveFolderTemplateRequest,
    thunkAPI,
  ) => {
    try {
      await api.delete(`/docvault/folders/${folder_id}/templates/`, {
        data: { templates: [template_slug] },
      });
      return template_slug;
    } catch (error: any) {
      const msg = getExceptionMessage(error.response.data?.code);
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const downloadFolderAsZip = createAsyncThunk(
  "folders/downloadFolderAsZip",
  async (
    { folder_id, folder_number }: OnDownloadFolderAsZipRequest,
    thunkAPI,
  ) => {
    try {
      const response = await api.get(
        `/docvault/folders/${folder_id}/download`,
        {
          responseType: "blob",
        },
      );

      const downloadedZip: Blob = response.data;

      const zipURL = URL.createObjectURL(downloadedZip);
      const link = document.createElement("a");

      link.href = zipURL;
      link.download = `expediente-${folder_number || folder_id}.zip`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(zipURL);
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.downloadFolderAsZip,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const sendFolderStatusEmail = createAsyncThunk(
  "folders/sendFolderStatusEmail",
  async (folder_id: string, thunkAPI) => {
    try {
      await api.post(`/docvault/folders/${folder_id}/send-email/`);
      return folder_id;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        folderExceptions.sendFolderStatusEmail,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchMyFolders = createAsyncThunk(
  "folders/fetchMyFolders",
  async (params: string | object = "/docvault/folders/user/", thunkAPI) => {
    try {
      // If params is a string, treat it as a URL (used for pagination)
      if (typeof params === "string") {
        const response = await api.get<FolderResult>(params);
        return response.data;
      }

      // Otherwise, treat it as a params object (used for filtering)
      const response = await api.get<FolderResult>("/docvault/folders/user/", {
        params,
      });
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response?.data?.code,
        folderExceptions.fetchAllFolders,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchFolderPdf = createAsyncThunk(
  "folders/fetchFolderPdf",
  async (id: number, thunkAPI) => {
    try {
      const response = await api.get(`docvault/folders/${id}/preview-report`, {
        responseType: "blob",
      });
      const downloadedFile: Blob = response.data;
      const pdfBlob = new Blob([downloadedFile], { type: "application/pdf" });

      return URL.createObjectURL(pdfBlob);
    } catch (error: any) {
      const defaultErrorCode = "3999";

      if (error.response?.data) {
        const errorBlob = error.response.data;
        const errorText = await errorBlob.text();
        const errorJson = JSON.parse(errorText);

        const errorCode = errorJson.code?.toString();
        let errorMessage =
          folderExceptionsMessage[errorCode || defaultErrorCode];

        if (errorCode === "3998") {
          errorMessage = errorJson.message;
        }

        return thunkAPI.rejectWithValue(errorMessage);
      }

      return thunkAPI.rejectWithValue(
        folderExceptionsMessage[defaultErrorCode],
      );
    }
  },
);

export const savePdfUrl = createAsyncThunk(
  "folders/savePdfUrl",
  async ({ folderNumber, url }: { folderNumber: number; url: string }) => {
    return { folderNumber, url };
  },
);

export const completeFileFolder = createAsyncThunk(
  "folders/completeFileFolder",
  async (id: number, thunkAPI) => {
    const defaultErrorCode = "3999";

    try {
      await api.get<CompleteFileFolderResponse>(
        `docvault/folders/${id}/complete-report`,
        { responseType: "json" },
      );
    } catch (error: any) {
      const errorCode = error.response?.data?.code?.toString();
      return thunkAPI.rejectWithValue({
        code: errorCode || defaultErrorCode,
        message: folderExceptionsMessage[errorCode || defaultErrorCode],
      });
    }
  },
);
